import { useState } from 'react';
import './RoverPlacementModal.css';

export interface RoverType {
  id: string;
  name: string;
  description: string;
  cost: number; // Outfitting cost in Cyber Tokens
  produces: string; // Resource type
  icon: string; // Icon identifier
  terrainSuitability?: string[]; // Best terrain types
}

// Rover types based on RESOURCE_PRODUCTION_MECHANICS.md
export const ROVER_TYPES: RoverType[] = [
  {
    id: 'food',
    name: 'Food Rover',
    description: 'Greenhouse configuration for food production',
    cost: 50,
    produces: 'food',
    icon: 'food-8bit.png',
    terrainSuitability: ['crater', 'lunar'],
  },
  {
    id: 'energy',
    name: 'Energy Rover',
    description: 'Solar dish configuration for energy generation',
    cost: 25,
    produces: 'energy',
    icon: 'energy-8bit.png',
    terrainSuitability: ['highland', 'mountain'],
  },
  {
    id: 'mining',
    name: 'Mining Rover',
    description: 'Can mine oxygen, hydrogen, or metals',
    cost: 40,
    produces: 'minerals',
    icon: 'minerals-8bit.png',
    terrainSuitability: ['mountain', 'highland'],
  },
  {
    id: 'water',
    name: 'Water Rover',
    description: 'Water processing facility',
    cost: 35,
    produces: 'water',
    icon: 'oxygen-8bit.png',
    terrainSuitability: ['crater'],
  },
  {
    id: 'helium3',
    name: 'Helium-3 Rover',
    description: 'Extraction facility for helium-3',
    cost: 45,
    produces: 'helium3',
    icon: 'helium3-8bit.png',
    terrainSuitability: ['regolith', 'lunar'],
  },
];

interface RoverPlacementModalProps {
  isOpen: boolean;
  plotTerrain: string;
  playerMoney: number; // Cyber Tokens
  availableRovers: number; // Number of rovers player has
  onPlaceRover: (roverType: RoverType) => void;
  onCancel: () => void;
}

export default function RoverPlacementModal({
  isOpen,
  plotTerrain,
  playerMoney,
  availableRovers,
  onPlaceRover,
  onCancel,
}: RoverPlacementModalProps) {
  const [selectedRover, setSelectedRover] = useState<RoverType | null>(null);

  if (!isOpen) return null;

  const canAfford = (roverType: RoverType) => playerMoney >= roverType.cost;
  const isSuitable = (roverType: RoverType) =>
    !roverType.terrainSuitability || 
    roverType.terrainSuitability.includes(plotTerrain);

  const handleConfirm = () => {
    if (selectedRover && canAfford(selectedRover)) {
      onPlaceRover(selectedRover);
    }
  };

  return (
    <div className="rover-placement-modal-overlay" onClick={onCancel}>
      <div 
        className="rover-placement-modal" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>🤖 Place Rover</h2>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        <div className="modal-content">
          <div className="modal-info">
            <div className="info-row">
              <span>Terrain:</span>
              <strong>{plotTerrain}</strong>
            </div>
            <div className="info-row">
              <span>Your Money:</span>
              <strong>{playerMoney} CT</strong>
            </div>
            <div className="info-row">
              <span>Available Rovers:</span>
              <strong>{availableRovers}</strong>
            </div>
          </div>

          {availableRovers <= 0 ? (
            <div className="warning-message">
              ⚠️ No rovers available! Visit the store to purchase more.
            </div>
          ) : (
            <>
              <div className="rover-types-grid">
                {ROVER_TYPES.map((roverType) => {
                  const affordable = canAfford(roverType);
                  const suitable = isSuitable(roverType);
                  const isSelected = selectedRover?.id === roverType.id;

                  return (
                    <div
                      key={roverType.id}
                      className={`rover-type-card ${isSelected ? 'selected' : ''} ${
                        !affordable || !suitable ? 'disabled' : ''
                      }`}
                      onClick={() => {
                        if (affordable && suitable) {
                          setSelectedRover(roverType);
                        }
                      }}
                    >
                      <div className="rover-icon">
                        {/* TODO: Load actual icon from S3 */}
                        {roverType.icon.replace('-8bit.png', '').toUpperCase()[0]}
                      </div>
                      <h3>{roverType.name}</h3>
                      <p className="rover-description">{roverType.description}</p>
                      <div className="rover-cost">
                        Cost: <strong>{roverType.cost} CT</strong>
                      </div>
                      <div className="rover-produces">
                        Produces: <strong>{roverType.produces}</strong>
                      </div>
                      {!suitable && (
                        <div className="unsuitable-label">Not ideal for {plotTerrain}</div>
                      )}
                      {!affordable && (
                        <div className="unaffordable-label">Cannot afford</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="modal-actions">
                <button className="cancel-btn" onClick={onCancel}>
                  Cancel
                </button>
                <button
                  className="confirm-btn"
                  onClick={handleConfirm}
                  disabled={!selectedRover || !canAfford(selectedRover)}
                >
                  Place {selectedRover?.name || 'Rover'} ({selectedRover?.cost || 0} CT)
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

